import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { Star } from 'lucide-react';
import Analytics from '../services/analytics';
const ProductCard = ({ product, badge, isNew, isExclusive }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const previewRef = useRef(null);
  const { addToWishlist, removeFromWishlist, isWishlisted } = useWishlist();
  const { addToCart, cartItems } = useCart();
  const productId = product.id;
  const wishlisted = isWishlisted(productId);
  const exclusiveFlag = isExclusive ?? product.is_exclusive;
  // Determine out-of-stock status
  const isOutOfStock = product.has_variants
    ? (product.total_stock || 0) <= 0
    : (product.stock_quantity || 0) <= 0;
  // Check if item already in cart (product_id match works because single-variant/simple only one possible)
  const isInCart = cartItems?.some(item => Number(item.product_id) === Number(product.id));
  // Determine which action button to show
  const showSelectOptions = product.has_variants && (product.total_variants || 0) > 1;
  const showAddToCartButton = !product.has_variants || (product.total_variants || 0) === 1;
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeOverlay();
    };
    if (showFullscreen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFullscreen]);
  const openOverlay = (e) => {
    if (e) e.stopPropagation();
    setShowFullscreen(true);
  };
  const closeOverlay = () => {
    setShowFullscreen(false);
  };
  useEffect(() => {
    if (showFullscreen && !previewRef.current) {
      const overlay = document.createElement('div');
      overlay.id = 'fullscreen-overlay-' + product.name.replace(/\s+/g, '-');
      overlay.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.8);
      `;
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '×';
      closeBtn.style.cssText = `
        position: absolute;
        top: 16px;
        right: 16px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        font-size: 24px;
        cursor: pointer;
      `;
      closeBtn.onclick = closeOverlay;
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.name;
      img.style.cssText = `
        max-width: 90vw;
        max-height: 85vh;
        object-fit: contain;
      `;
      overlay.appendChild(closeBtn);
      overlay.appendChild(img);
      overlay.onclick = closeOverlay;
      document.body.appendChild(overlay);
      previewRef.current = overlay;
    }
    return () => {
      if (previewRef.current) {
        previewRef.current.remove();
        previewRef.current = null;
      }
    };
  }, [showFullscreen, product.image, product.name]);
  return (
    <article className="group bg-[#fafafa] rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col h-full" aria-label={`${product.name} by ${product.brand}`}>
      <div className="relative aspect-square overflow-hidden bg-gray-100 flex-shrink-0">
        <button
          onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
          className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold focus:ring-inset"
          aria-label={`View ${product.name} details`}
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (wishlisted) {
              removeFromWishlist(productId);
            } else {
              addToWishlist({ ...product, id: productId });
              Analytics.wishlistAdd(product);
            }
          }}
          aria-label={wishlisted ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
          aria-pressed={wishlisted}
          className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 z-30 focus:outline-none focus:ring-2 focus:ring-gold"
        >
          <svg className={`w-4 h-4 ${wishlisted ? 'text-gold fill-current' : 'text-gray-400'}`} fill={wishlisted ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
        {/* Badges */}
        {(badge || !!isNew || !!exclusiveFlag) && (
          <div className="absolute top-3 left-3 flex gap-2">
            {!!isNew && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                NEW
              </span>
            )}
            {!!exclusiveFlag && (
              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                ONLINE EXCLUSIVE
              </span>
            )}
            {badge && (
              <span className="bg-gold text-white text-xs font-bold px-2 py-1 rounded">
                {badge}
              </span>
            )}
          </div>
        )}
        {/* Out of Stock overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
            <span className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
              Out of Stock
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 flex items-center justify-center">
          <button
            onClick={openOverlay}
            aria-label={`Preview ${product.name}`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gold text-white px-4 py-2 text-sm font-medium flex items-center gap-2 rounded shadow-lg hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </button>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <button
          onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
          className="text-left focus:outline-none focus:underline"
          aria-label={`View ${product.name} details`}
        >
          <p className="text-[10px] uppercase tracking-widest text-[#999] mb-1">{product.brand}</p>
          <h3 className="text-base font-semibold text-[#1a1a1a] mb-1 line-clamp-2">
            {product.name}
          </h3>
          {(product.average_rating > 0) && (
            <div className="flex items-center gap-1 mt-1">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3 h-3 ${
                      star <= Math.round(product.average_rating)
                        ? 'fill-gold text-gold'
                        : 'fill-gray-200 text-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                ({product.review_count})
              </span>
            </div>
          )}
          {/* Price display — handles simple, variant, and discounted products */}
          <div className="mb-3">
            {product.discount_label ? (
              // ON SALE — show strikethrough original + sale price + badge
              <>
                {/* Original price — strikethrough */}
                <p className="text-xs text-gray-400 line-through leading-tight">
                  {product.has_variants === 1 && product.min_price !== product.max_price
                    ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.max_price || 0).toLocaleString('en-US')}`
                    : product.has_variants === 1
                      ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')}`
                      : `KSh ${Number(product.price || 0).toLocaleString('en-US')}`
                  }
                </p>
                {/* Sale price + badge */}
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <p className="text-sm font-semibold text-gold">
                    {product.has_variants === 1 && product.sale_min_price !== product.sale_max_price
                      ? `KSh ${Number(product.sale_min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.sale_max_price || 0).toLocaleString('en-US')}`
                      : product.has_variants === 1
                        ? `KSh ${Number(product.sale_min_price || 0).toLocaleString('en-US')}`
                        : `KSh ${Number(product.sale_price || 0).toLocaleString('en-US')}`
                    }
                  </p>
                  <span className="bg-amber-500 text-white text-xs font-bold
                    px-2 py-0.5 rounded">
                    {product.discount_label}
                  </span>
                </div>
              </>
            ) : (
              // NO DISCOUNT — original price display unchanged
              <p className="text-sm font-semibold text-gold">
                {product.has_variants === 1 && product.min_price !== product.max_price
                  ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.max_price || 0).toLocaleString('en-US')}`
                  : product.has_variants === 1
                    ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')}`
                    : `KSh ${Number(product.price || 0).toLocaleString('en-US')}`
                }
              </p>
            )}
          </div>
        </button>
        <div className="border-t border-[#f0f0f0] pt-3 mt-auto">
          <div className="flex flex-col md:flex-row gap-2 transition-opacity duration-200">
            {showSelectOptions ? (
               <Link
                  to={`/product/${product.id}`}
                  className="flex-1 py-3 flex items-center justify-center rounded-md relative group/btn overflow-hidden bg-gold hover:bg-yellow-500 text-white focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 transition-colors"
                  onClick={e => e.stopPropagation()}
                >
                  Select Options
                </Link>
            ) : showAddToCartButton ? (
              <button
                onClick={async () => {
                  if (addingToCart || isInCart || isOutOfStock) return;
                  setAddingToCart(true);
                  try {
                    if (product.has_variants && product.total_variants === 1) {
                      try {
                        const res = await fetch(
                          `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/products/${product.id}/variants`
                        );
                        const variants = await res.json();
                        if (variants.length > 0) {
                          const variant = variants[0];
                          // Stock check
                          if (Number(variant.stock_quantity) <= 0) {
                            toast.error('Sorry, this item is out of stock');
                            return;
                          }
                          await addToCart({
                            ...product,
                            quantity: 1,
                            variant_id: variant.id,
                            sku: variant.variant_sku,
                            color: variant.color,
                            size: variant.size,
                            finalPrice: Number(product.price || 0) + Number(variant.price_adjustment || 0),
                            base_price: Number(product.price || 0) + Number(variant.price_adjustment || 0),
                            variant_stock_quantity: variant.stock_quantity
                          });
                        }
                      } catch (err) {
                      await addToCart({
                        ...product,
                        finalPrice: product.sale_price || product.price,
                        base_price: product.price,
                      });
                      }
                    } else {
                      // Simple product
                      if (Number(product.stock_quantity) <= 0) {
                        toast.error('Sorry, this item is out of stock');
                        return;
                      }
                      await addToCart({
                        ...product,
                        finalPrice: product.sale_price || product.price,
                        base_price: product.price,
                      });
                    }
                    Analytics.addToCart(product);
                  } catch (err) {
                    console.error('Add to cart failed:', err);
                  } finally {
                    setAddingToCart(false);
                  }
                }}
                aria-label={`Add ${product.name} to cart${isInCart ? ' (already in cart)' : ''}`}
                disabled={isOutOfStock || addingToCart || isInCart}
                 className={`flex-1 py-3 flex items-center justify-center rounded-md relative group/btn overflow-hidden focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 transition-colors ${isOutOfStock
                     ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                     : isInCart
                       ? 'bg-green-500 cursor-default text-white'
                       : addingToCart
                         ? 'bg-gray-400 cursor-wait text-white'
                          : 'bg-gold hover:bg-yellow-500 text-white'
                   }`}
              >
                <span className="text-white text-xs font-medium tracking-wide absolute w-full text-center transition-all duration-300 -translate-y-full group-hover/btn:translate-y-0 group-hover/btn:opacity-100 opacity-0" aria-hidden="true">
                  {isOutOfStock
                    ? 'Out of Stock'
                    : isInCart
                      ? '✓ In Cart'
                      : addingToCart
                        ? 'Adding...'
                        : 'Add to Cart'}
                </span>
                <svg className="w-5 h-5 text-white transition-all duration-300 group-hover/btn:translate-y-full group-hover/btn:opacity-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 8a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};
export default ProductCard;